import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/testimonials`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch testimonials');
      }
      
      const data = await response.json();
      setTestimonials(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={18}
            fill={star <= rating ? 'var(--brand-primary)' : 'transparent'}
            style={{ 
              color: star <= rating ? 'var(--brand-primary)' : '#6B7280',
              strokeWidth: 2
            }}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <section className="py-20" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="container">
          <div className="text-center">
            <p className="text-gray-400">Loading testimonials...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || testimonials.length === 0) {
    return null; // Don't show section if there's an error or no testimonials
  }

  return (
    <section className="py-20" style={{ backgroundColor: '#0A0A0A' }}>
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4" style={{ color: 'var(--brand-primary)' }}>
            What Our Patients Say
          </h2>
          <p className="body-medium text-gray-400 max-w-2xl mx-auto">
            Real experiences from patients who've achieved their recovery goals with personalized physical therapy care.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="testimonial-card p-6 rounded-lg transition-all duration-300 hover:transform hover:-translate-y-1"
              style={{
                backgroundColor: '#141414',
                border: '1px solid rgba(232, 184, 109, 0.1)'
              }}
            >
              {/* Quote Icon */}
              <Quote 
                size={32} 
                style={{ color: 'var(--brand-primary)', opacity: 0.3 }} 
                className="mb-4"
              />

              {/* Rating Stars */}
              {renderStars(testimonial.rating)}

              {/* Testimonial Text */}
              <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                "{testimonial.text}"
              </p>

              {/* Patient Name */}
              <div className="flex items-center justify-between">
                <p className="font-semibold" style={{ color: 'var(--brand-primary)' }}>
                  {testimonial.name}
                </p>
                {testimonial.source === 'google_business' && (
                  <span className="text-xs text-gray-500">via Google</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
